import { useSelector } from 'react-redux'
import { useScrollBottom } from 'hooks/useScrollBottom'
import { BotTyping } from 'components/BotTyping'
import { messagesSelectors, selectMessages } from 'store/messages'
import clsx from 'clsx'
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { ChatScrollContext } from 'components/chats/chatScrollContext'
import { settingsSelectors } from 'store/settings'
import { getChatMessages } from 'api/chats'
import { TicketReminderCard } from 'components/ui/TicketReminderCard'
import { Spinner } from 'components/ui/Spinner'
import { MessagesDiv } from 'components/ui/MessageDiv'
import { ChatMessages } from 'components/chats/ChatMessages'
import { DefaultQuestions } from 'components/DefaultQuestions'
import { useGetSetting } from 'api/settings'
import { useChatWebSocket } from 'hooks/useChatWebSocket'
import { useAgenticBootstrap } from 'hooks/useAgenticBootstrap'

type ChatDetailProps = {
  /** The chat page passes false: its sidebar already owns the user WS channel, and a double subscribe would duplicate handlers. */
  subscribeUserChannel?: boolean
  /** Called after every message load (chat switches included); the chat page uses it to flush the hero's pending question. */
  onMessagesLoaded?: () => void
}

const ChatDetailComponent = ({ subscribeUserChannel = true, onMessagesLoaded }: ChatDetailProps) => {
  const { setting, numberOfQuestionsBeforeAskTicketOpen } = useGetSetting()
  const messages = useSelector(selectMessages)
  const chatId = useSelector(messagesSelectors.chatId)
  const currentChatBotId = useSelector(messagesSelectors.currentChatBotId)
  const [loading, setLoading] = useState(true)
  const containerRef = useScrollBottom(messages)
  const currentUserId = useSelector(settingsSelectors.userId)

  const onMessagesLoadedRef = useRef(onMessagesLoaded)
  onMessagesLoadedRef.current = onMessagesLoaded

  const scrollToBottom = useCallback(() => {
    const el = containerRef.current
    el?.scrollTo({ top: el.scrollHeight, behavior: 'smooth' })
  }, [containerRef])
  const scrollValue = useMemo(() => ({ scrollToBottom }), [scrollToBottom])

  useChatWebSocket({
    chatId,
    userId: subscribeUserChannel ? currentUserId : undefined,
    currentChatBotId,
    currentUserId,
    numberOfQuestionsBeforeAskTicketOpen,
    onScrollToBottom: scrollToBottom,
  })

  useAgenticBootstrap()

  useEffect(() => {
    let cancelled = false
    if (chatId) {
      setLoading(true)
      getChatMessages(chatId).then(() => {
        if (cancelled) return
        setLoading(false)
        onMessagesLoadedRef.current?.()
      })
    }
    return () => {
      cancelled = true
    }
  }, [chatId])

  useLayoutEffect(() => {
    if (loading) return
    const el = containerRef.current
    if (el) el.scrollTop = el.scrollHeight
  }, [loading])

  return (
    <ChatScrollContext.Provider value={scrollValue}>
      <MessagesDiv
        ref={containerRef}
        className={clsx(
          'tw-flex tw-w-full tw-flex-1 tw-min-h-0 tw-flex-col tw-self-start tw-overflow-y-auto tw-bg-transparent tw-p-[12px]',
        )}
        widgetColor={setting?.primaryColor}
      >
        {loading ? (
          <Spinner />
        ) : (
          <>
            <ChatMessages />
            <DefaultQuestions />
            <BotTyping />
            <TicketReminderCard />
          </>
        )}
      </MessagesDiv>
    </ChatScrollContext.Provider>
  )
}

export const ChatDetail = memo(ChatDetailComponent)
